export const MINUTA_TEMPLATE = `<!DOCTYPE html>
<html lang="es">

<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>{{TITLE}} — Minuta IA de Kira</title>
  <link rel="preconnect" href="https://fonts.googleapis.com">
  <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
  <link href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&family=IBM+Plex+Mono:wght@400;500&display=swap" rel="stylesheet" />
  <style>
    :root {
      /* macOS Dark Mode Color Palette */
      --bg: #0d0d0f;
      --window-bg: #1e1e22;
      --surface: rgba(255, 255, 255, 0.02);
      --surface-hover: rgba(255, 255, 255, 0.05);
      --border: rgba(255, 255, 255, 0.08);
      --accent: #007aff;
      --accent-muted: rgba(0, 122, 255, 0.15);
      --text: #f5f5f7;
      --text-muted: #a1a1a6;
      --text-dim: #6e6e73;

      /* macOS Window Traffic Lights */
      --mac-close: #ff5f56;
      --mac-minimize: #ffbd2e;
      --mac-maximize: #27c93f;
    }

    *,
    *::before,
    *::after {
      box-sizing: border-box;
      margin: 0;
      padding: 0;
    }

    body {
      background-color: var(--bg);
      color: var(--text);
      font-family: 'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
      font-size: 14px;
      line-height: 1.6;
      display: flex;
      justify-content: center;
      padding: 40px 20px;
      min-height: 100vh;
    }

    /* macOS Window Frame */
    .window {
      background: var(--window-bg);
      border: 1px solid var(--border);
      border-radius: 12px;
      width: 100%;
      max-width: 800px;
      box-shadow: 0 30px 70px rgba(0, 0, 0, 0.5);
      overflow: hidden;
      display: flex;
      flex-direction: column;
      position: relative;
    }

    /* macOS Titlebar */
    .titlebar {
      height: 48px;
      background: rgba(30, 30, 34, 0.8);
      border-bottom: 1px solid var(--border);
      display: flex;
      align-items: center;
      padding: 0 16px;
      justify-content: space-between;
      position: sticky;
      top: 0;
      z-index: 10;
      backdrop-filter: blur(10px);
      -webkit-backdrop-filter: blur(10px);
    }

    .traffic-lights {
      display: flex;
      gap: 8px;
      align-items: center;
    }

    .traffic-lights span {
      width: 12px;
      height: 12px;
      border-radius: 50%;
      display: inline-block;
    }

    .traffic-lights .close { background-color: var(--mac-close); }
    .traffic-lights .minimize { background-color: var(--mac-minimize); }
    .traffic-lights .maximize { background-color: var(--mac-maximize); }

    .window-title {
      font-size: 12px;
      font-weight: 500;
      color: var(--text-muted);
      font-family: 'IBM Plex Mono', monospace;
      letter-spacing: 0.05em;
      text-transform: uppercase;
    }

    .window-actions {
      display: flex;
      gap: 8px;
    }

    .btn-action {
      background: rgba(255, 255, 255, 0.05);
      border: 1px solid var(--border);
      color: var(--text-muted);
      font-size: 11px;
      font-weight: 500;
      padding: 4px 10px;
      border-radius: 6px;
      cursor: pointer;
      display: flex;
      align-items: center;
      gap: 6px;
      transition: all 0.2s ease;
      font-family: inherit;
    }

    .btn-action:hover {
      background: rgba(255, 255, 255, 0.1);
      color: var(--text);
      border-color: rgba(255, 255, 255, 0.15);
    }

    /* Document Body */
    .document-body {
      padding: 40px 50px;
      overflow-y: auto;
    }

    @media (max-width: 600px) {
      .document-body {
        padding: 24px 20px;
      }
    }

    /* Header info block */
    .doc-header {
      margin-bottom: 30px;
      border-bottom: 1px dashed var(--border);
      padding-bottom: 24px;
    }

    .doc-label {
      font-family: 'IBM Plex Mono', monospace;
      font-size: 10px;
      font-weight: 600;
      color: var(--accent);
      text-transform: uppercase;
      letter-spacing: 0.15em;
      margin-bottom: 6px;
      display: inline-block;
    }

    h1 {
      font-size: 26px;
      font-weight: 800;
      color: var(--text);
      letter-spacing: -0.03em;
      line-height: 1.25;
      margin-bottom: 16px;
    }

    /* Metadata grid */
    .metadata-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
      gap: 12px;
      background: var(--surface);
      border: 1px solid var(--border);
      border-radius: 8px;
      padding: 16px;
      margin-top: 16px;
    }

    .meta-item {
      display: flex;
      flex-direction: column;
      gap: 4px;
    }

    .meta-label {
      font-size: 9px;
      font-weight: 600;
      color: var(--text-dim);
      text-transform: uppercase;
      letter-spacing: 0.08em;
    }

    .meta-value {
      font-size: 12.5px;
      font-weight: 500;
      color: var(--text-muted);
    }

    /* Document Content elements */
    .doc-content h2 {
      font-size: 17px;
      font-weight: 700;
      color: var(--text);
      border-bottom: 1px solid var(--border);
      padding-bottom: 6px;
      margin: 32px 0 14px;
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .doc-content h2:first-child {
      margin-top: 0;
    }

    .doc-content h3 {
      font-size: 14px;
      font-weight: 600;
      color: var(--accent);
      margin: 20px 0 8px;
    }

    .doc-content p {
      font-size: 13.5px;
      color: var(--text-muted);
      line-height: 1.6;
      margin-bottom: 14px;
    }

    .doc-content ul {
      margin-bottom: 16px;
      padding-left: 20px;
    }

    .doc-content li {
      margin-bottom: 8px;
      color: var(--text-muted);
      line-height: 1.5;
    }

    /* Action items table */
    .doc-content table {
      width: 100%;
      border-collapse: collapse;
      margin: 18px 0;
      border: 1px solid var(--border);
      border-radius: 8px;
      overflow: hidden;
    }

    .doc-content th {
      background: rgba(255, 255, 255, 0.02);
      border-bottom: 1px solid var(--border);
      color: var(--text);
      font-size: 11.5px;
      font-weight: 600;
      text-align: left;
      padding: 10px 14px;
      text-transform: uppercase;
      letter-spacing: 0.04em;
    }

    .doc-content td {
      border-bottom: 1px solid var(--border);
      color: var(--text-muted);
      font-size: 12.5px;
      padding: 10px 14px;
      line-height: 1.4;
    }

    .doc-content tr:last-child td {
      border-bottom: none;
    }

    .doc-content tr:hover td {
      background: rgba(255, 255, 255, 0.01);
    }

    /* Status badges */
    .badge {
      display: inline-block;
      font-size: 9.5px;
      font-weight: 600;
      text-transform: uppercase;
      padding: 2px 6px;
      border-radius: 4px;
      letter-spacing: 0.02em;
    }

    .badge-pending {
      background: rgba(255, 69, 58, 0.1);
      color: #ff453a;
    }

    .badge-done {
      background: rgba(48, 209, 88, 0.1);
      color: #30d158;
    }

    .badge-progress {
      background: rgba(0, 122, 255, 0.1);
      color: #007aff;
    }

    /* Footer stamp */
    .doc-footer {
      margin-top: 40px;
      border-top: 1px solid var(--border);
      padding-top: 20px;
      display: flex;
      justify-content: space-between;
      align-items: center;
      font-family: 'IBM Plex Mono', monospace;
      font-size: 9.5px;
      color: var(--text-dim);
    }
  </style>
</head>

<body>
  <div class="window">
    <!-- macOS Window Header / Titlebar -->
    <div class="titlebar">
      <div class="traffic-lights">
        <span class="close"></span>
        <span class="minimize"></span>
        <span class="maximize"></span>
      </div>
      <div class="window-title">Kira — Minuta IA</div>
      <div class="window-actions">
        <button class="btn-action" onclick="window.print()">
          <span>🖨️</span> Imprimir
        </button>
      </div>
    </div>

    <!-- Main document sheet -->
    <div class="document-body">
      <div class="doc-header">
        <span class="doc-label">MINUTA DE REUNIÓN GENERAL</span>
        <h1>{{TITLE}}</h1>
        
        <!-- Metadata row -->
        <div class="metadata-grid">
          <div class="meta-item">
            <span class="meta-label">Fecha</span>
            <span class="meta-value" id="doc-date">{{DATE}}</span>
          </div>
          <div class="meta-item">
            <span class="meta-label">Proyecto</span>
            <span class="meta-value">Kira AI / Kira</span>
          </div>
          <div class="meta-item">
            <span class="meta-label">Generador</span>
            <span class="meta-value">Claude 3.5 Sonnet (AWS Bedrock)</span>
          </div>
        </div>
      </div>

      <!-- Main content injected dynamically -->
      <div class="doc-content" id="doc-content">
        {{CONTENT}}
      </div>

      <!-- Footer system stamp -->
      <div class="doc-footer">
        <span>MINUTA IA — Kira LABS</span>
        <span>CONFIDENCIAL</span>
      </div>
    </div>
  </div>

  <script>
    // Configura la fecha actual por defecto si no es reemplazada
    const dateEl = document.getElementById('doc-date');
    if (dateEl && dateEl.textContent.trim() === '{{DATE}}') {
      const options = { year: 'numeric', month: 'long', day: 'numeric' };
      dateEl.textContent = new Date().toLocaleDateString('es-ES', options);
    }
  </script>
</body>

</html>`;
